<script setup lang="ts">
import { ref } from "vue";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";

interface LightboxImage {
    src: string;
    alt: string;
}

defineProps<{
    selectedImage: LightboxImage | null;
}>();

const emit = defineEmits<{
    close: [];
}>();

const lightboxDialog = ref<HTMLDialogElement | null>(null);

// Watch for selectedImage changes to open/close dialog
const openLightbox = () => {
    lightboxDialog.value?.showModal();
};

const closeLightbox = () => {
    emit("close");
    lightboxDialog.value?.close();
};

// Expose methods for parent component
defineExpose({
    openLightbox,
    closeLightbox,
});
</script>

<template>
    <dialog
        ref="lightboxDialog"
        class="backdrop:bg-black backdrop:bg-opacity-75 bg-transparent p-0 max-w-none max-h-none w-full h-full"
        @click.self="closeLightbox"
    >
        <Transition
            enter-active-class="transition-all duration-300"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition-all duration-300"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div
                v-if="selectedImage"
                class="relative flex items-center justify-center min-h-full p-4"
            >
                <!-- Close button -->
                <button
                    @click="closeLightbox"
                    class="absolute top-4 right-4 z-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full p-3 transition-all duration-200"
                    aria-label="Close lightbox"
                >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <!-- Lightbox image -->
                <Image
                    :src="selectedImage.src"
                    :alt="selectedImage.alt"
                    class="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
                />
            </div>
        </Transition>
    </dialog>
</template>
